import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useRentmanData } from '../hooks/useRentmanData';

// Create context outside of component to prevent recreation on re-renders
const AppContext = createContext(undefined);

// Stable default value to prevent Fast Refresh issues
const defaultContextValue = {
  rentmanData: null,
  loading: true,
  error: null,
  rentmanLoading: true,
  isCartOpen: false,
  openCart: () => {},
  closeCart: () => {},
  toggleCart: () => {},
  isCartPinned: true,
  setIsCartPinned: () => {},
  handleCartClose: () => {},
};

export function AppProvider({ children }) {
  // Rentman data state
  const { data, loading: rentmanLoading, error } = useRentmanData();
  
  // Cart sidebar state
  const [isCartOpen, setIsCartOpen] = useState(false);
  // Cart pin state (for sidebar)
  const [isCartPinned, setIsCartPinned] = useState(true);

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);
  const toggleCart = useCallback(() => setIsCartOpen(prev => !prev), []);

  // Central loading state - only depends on Rentman data
  const isLoading = rentmanLoading;

  // Stable cart close handler using latest isCartPinned
  const handleCartClose = useCallback(() => {
    if (isCartPinned) {
      setIsCartPinned(false);
    }
    closeCart();
  }, [isCartPinned, closeCart]);

  // Auto-unpin cart when screen width is less than 1300px
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1299px)');
    const handleResize = (e) => {
      if (e.matches) {
        // Always unpin when screen is narrow
        setIsCartPinned(false);
      }
    };

    // Initial check and setup
    handleResize(mediaQuery);
    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, []); // Remove isCartPinned from dependencies to prevent unnecessary re-renders

  // Memoized context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // Data
    rentmanData: data,
    loading: isLoading,
    error,
    
    // Individual loading states
    rentmanLoading,

    // Cart sidebar state
    isCartOpen,
    openCart,
    closeCart,
    toggleCart,

    // Cart pin state
    isCartPinned,
    setIsCartPinned,

    // Cart close handler
    handleCartClose,
  }), [
    data, 
    isLoading, 
    error, 
    rentmanLoading, 
    isCartOpen, 
    openCart, 
    closeCart, 
    toggleCart, 
    isCartPinned, 
    setIsCartPinned, 
    handleCartClose
  ]);

  // Prevent re-renders in development mode that cause Fast Refresh issues
  if (import.meta.env.DEV && !data && rentmanLoading) {
    // In development, show a stable loading state to prevent Fast Refresh issues
    return (
      <AppContext.Provider value={value} key="app-provider-stable">
        {children}
      </AppContext.Provider>
    );
  }

  return (
    <AppContext.Provider value={value} key="app-provider-stable">
      {children}
    </AppContext.Provider>
  );
}

// Custom hook to use the app context with better error handling
export function useApp() {
  const context = useContext(AppContext);
  
  // In development, provide helpful error messages
  if (import.meta.env.DEV && context === undefined) {
    console.error('useApp must be used within an AppProvider. Check your component hierarchy.');
    // Return default values in development to prevent crashes
    return defaultContextValue;
  }
  
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  
  return context;
} 